import React, { forwardRef, useImperativeHandle, useState } from 'react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Eye, Heart, Home, Info, MapPin, Edit2, UserPlus, AlertCircle, Check, X, Clock } from 'lucide-react'
import { Card } from '@/components/ui/card';
import type { PropertyListings } from '@/types/PropertyListings';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import Helper from '@/lib/helper';
import { useSelector } from 'react-redux';
import type { RootState } from '@/lib/redux/store';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Trash2 } from 'lucide-react';
import LabelWithAsterisk from '@/components/LabelWithAsterisk';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/components/DatePicker';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/switch';

interface OwnerForm {
    firstName: string;
    lastName: string;
    email: string;
    contact: string;
    dob: Date | undefined;
    requestSentAt?: string | null;
    status?: number | null;
    reason?: string | null;
    avatar?: string | null;
}

interface _PlatformPostingDetails {
    listing: PropertyListings;
    markAsDuplicate?: boolean;
    edit?: boolean;
    requireOwnerValidation: boolean;
    setRequireOwnerValidation: React.Dispatch<React.SetStateAction<boolean>>;
    owners: OwnerForm[];
    setOwners: React.Dispatch<React.SetStateAction<OwnerForm[]>>;
    ownerValidationErrors: {[key: number]: string[]};
    setOwnerValidationErrors: React.Dispatch<React.SetStateAction<{[key: number]: string[]}>>;
    isInternal: boolean;
    setIsInternal: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface PlatformPostingDetailsRef {
    callback: () => boolean;
}

const PlatformPostingDetails = forwardRef<PlatformPostingDetailsRef, _PlatformPostingDetails>(({
    listing,
    requireOwnerValidation,
    setRequireOwnerValidation,
    owners,
    setOwners,
    ownerValidationErrors,
    setOwnerValidationErrors,
    isInternal,
    setIsInternal
}, ref) => {
    const agent = useSelector((state: RootState) => state.auth.agent);
    const [ownerDialog, setOwnerDialog] = useState<boolean>(false);
    const [ownerError, setOwnerError] = useState<string | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);
    
    const ownerErrorText: { [key: string]: string } = {
        'firstName': 'First name is required.',
        'lastName': 'Last name is required.',
        'email': 'Email is required.',
        'email-format': 'Invalid email format.',
        'contact': 'Contact number is required.',
        'contact-format': 'Invalid contact number format.',
        'dob': 'Date of birth is required.',
    };

    const handleAddOwner = () => {
        setOwners([...owners, {
            firstName: '',
            lastName: '',
            email: '',
            contact: '',
            dob: undefined,
            requestSentAt: null,
            status: null,
            reason: null
        }]);
    };
    
    const handleRemoveOwner = (indexToRemove: number) => {
        if (owners.length > 1) {
            setOwners(owners.filter((_, i) => i !== indexToRemove));
            const newErrors = { ...ownerValidationErrors };
            delete newErrors[indexToRemove];
            const reindexedErrors: {[key: number]: string[]} = {};
            Object.keys(newErrors).forEach(key => {
                const numKey = parseInt(key);
                if (numKey > indexToRemove) {
                    reindexedErrors[numKey - 1] = newErrors[numKey];
                } else {
                    reindexedErrors[numKey] = newErrors[numKey];
                }
            });
            setOwnerValidationErrors(reindexedErrors);
        }
    };
    
    const handleOwnerChange = (
        ownerIndex: number,
        field: keyof OwnerForm,
        value: string | Date | undefined
    ) => {
        setOwners(prev => {
            const updated = [...prev];
            let newValue: string | Date | undefined = value;
            
            if (field === 'contact' && typeof value === 'string') {
                let formatted = value.replace(/\D/g, '');
                formatted = formatted.replace(/^60/, '').replace(/^0*/, '');
                if (!formatted.startsWith('1')) {
                    formatted = '1' + formatted;
                }
                newValue = '0' + formatted;
            }
            
            updated[ownerIndex] = {
                ...updated[ownerIndex],
                [field]: newValue
            };
            return updated;
        });
    
        setOwnerValidationErrors(prev => {
            const updated = { ...prev };
            if (updated[ownerIndex]) {
                updated[ownerIndex] = updated[ownerIndex].filter(err => err !== field);
            }
            return updated;
        });
    };
    
    const validateOwners = () => {
        const errors: {[key: number]: string[]} = {};
        let isValid = true;
    
        owners.forEach((owner, index) => {
            const ownerErrors: string[] = [];
    
            if (!owner.firstName.trim()) ownerErrors.push('firstName');
            if (!owner.lastName.trim()) ownerErrors.push('lastName');
            if (!owner.email.trim()) ownerErrors.push('email');
            else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(owner.email)) ownerErrors.push('email-format');
            
            if (!owner.contact.trim()) ownerErrors.push('contact');
            else if (!/^01\d{8,}$/.test(owner.contact.trim())) ownerErrors.push('contact-format');
    
            if (!owner.dob) ownerErrors.push('dob');
    
            if (ownerErrors.length > 0) {
                errors[index] = ownerErrors;
                isValid = false;
            }
        });
    
        setOwnerValidationErrors(errors);
        return isValid;
    };
    
    const getOwnerError = (ownerIndex: number, field: string) => {
        if (ownerValidationErrors[ownerIndex]?.includes(field)) {
            return ownerErrorText[field];
        }
        return '';
    };
    
    const handleOwnerSubmission = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setOwnerError(null);
    
        try {
            if (!validateOwners()) {
                throw new Error('Please complete all required fields for all owners.');
            }
            setOwnerDialog(false);
            setValidationError(null);
        } catch (err: any) {
            setOwnerError(err.message);
        }
    };

    const getWatermarkedImageUrl = (listingId: number, mediaId: number, agentId: number) => {
        return `${import.meta.env.VITE_API_URL}/listings/${listingId}/medias/${mediaId}?agentId=${agentId}`;
    };
    
    const callback = () => {
        if (requireOwnerValidation && owners.length === 0) {
            setValidationError('Please add at least one owner for validation.');
            return false;
        }
        
        if (requireOwnerValidation && owners.length > 0) {
            const allOwnersComplete = owners.every(owner => 
                owner.firstName.trim() !== '' &&
                owner.lastName.trim() !== '' &&
                owner.email.trim() !== '' &&
                /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(owner.email) &&
                owner.contact.trim() !== '' &&
                /^01\d{8,}$/.test(owner.contact.trim()) &&
                owner.dob !== undefined
            );
            if (!allOwnersComplete) {
                setValidationError('Please complete all owner details before proceeding.');
                return false;
            }
        }
        
        setValidationError(null);
        return true;
    }
    
    useImperativeHandle(ref, () => ({
        callback
    }));

    const formatDate = (date: Date | undefined) => {
        if (!date) return '';
        return new Date(date).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatDateTime = (dateString: string | null | undefined) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const dateStr = date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
        const timeStr = date.toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
        return `${dateStr} ${timeStr}`;
    };

    const getStatusBadge = (status: number | null | undefined) => {
        if (status === null || status === undefined) {
            return (
                <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 border-yellow-300">
                    <Clock className="w-3 h-3 mr-1" />
                    Pending
                </Badge>
            );
        }
        if (Number(status) === 1) {
            return (
                <Badge className="bg-green-100 text-green-800 border-green-300">
                    <Check className="w-3 h-3 mr-1" />
                    Approved
                </Badge>
            );
        }
        if (Number(status) === 0) {
            return (
                <Badge variant="destructive" className="bg-red-100 text-red-800 border-red-300">
                    <X className="w-3 h-3 mr-1" />
                    Rejected
                </Badge>
            );
        }
        return null;
    };

    const coverMedia = listing.medias.find(x => x.mediaType === 1 && 'isCover' in x && x.isCover === 1) ||
                       listing.medias.find(x => x.mediaType === 1);

    return listing && (
        <section>
            <Alert className='mt-3 border-amber-200 bg-amber-50'>
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-amber-900">Important Notice!</AlertTitle>
                <AlertDescription className="text-amber-800">
                    Please ensure all images and listing details are accurate before publishing. Once published, location and listing type cannot be edited. Inaccurate or misleading information may result in penalties including removal of the listing.
                </AlertDescription>
            </Alert>
            {
                validationError && (
                    <Alert variant="destructive" className="mt-3">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>Validation Error</AlertTitle>
                        <AlertDescription>{validationError}</AlertDescription>
                    </Alert>
                )
            }
            <div className='mt-5'>
                <h4 className='h4'>Review your listing</h4>
                <div className='bg-secondary rounded p-5 mt-3'>
                    <Card className='border'>
                        <div className='md:flex px-3 py-3'>
                            {
                                coverMedia ? (
                                    <div
                                        className="border md:w-[200px] h-[150px] w-full rounded bg-cover bg-center"
                                        style={{
                                            backgroundImage: `url(${getWatermarkedImageUrl(listing.id, Number(coverMedia.id), listing.createdBy ?? agent.id)})`,
                                        }}>
                                    </div>
                                ) : (
                                    <div className="border md:w-[200px] h-[150px] w-full bg-gray-200 rounded flex items-center justify-center text-sm text-black">
                                        <Spinner />
                                    </div>
                                )
                            }
                            <div className='md:px-3 relative w-full rounded'>
                                <div className='text-sm h6'>{ listing.headline }</div>
                                <div className=''>
                                    <div className='text-sm h6 text-right'>{ Helper.formatCurrency(Number(listing.price)) } </div>
                                    <div className='text-xs text-right'>({Helper.formatCurrency(Number(listing.price) / Number(listing.property.sizeOfLand))}/sqft)</div>
                                </div>
                                <div className='flex items-center mt-2'>
                                    <MapPin size={14}/> 
                                    <div className='text-xs ml-2'>{ listing.property.city?.name } / { listing.property.state?.name }</div>
                                </div>
                                <div className='flex items-center mt-2'>
                                    <Home size={14}/>
                                    <div className='text-xs ml-2'>{ listing.property.propertySubType?.name } / { listing.property.propertyType?.name }</div>
                                </div>
                                <div className='mt-2 flex justify-end items-center gap-3'>
                                    <div className='flex items-center'>
                                        <Eye size={14}/>
                                        <span className='text-xs ml-1'>1,000</span>
                                    </div>
                                    <div className={'flex items-center'}>
                                        <Heart size={14} color={'red'}/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
            <div className='mt-5 flex items-start gap-2'>
                <Switch 
                checked={requireOwnerValidation}
                onCheckedChange={(val) => {
                    const checked = !!val;
                    setRequireOwnerValidation(checked);
                    if (!checked) {
                        setValidationError(null);
                    }
                }}
                className="mt-1"
                disabled={listing.published ? true : false}/>
                <div className="flex-1">
                    <Label className="cursor-pointer">Require owner validation?</Label>
                    <p className="text-xs text-gray-500 mt-1">
                        Enable this to verify property ownership before listing publication
                    </p>
                </div>
            </div>
            <div className='mt-5 flex items-start gap-2'>
                <Switch 
                checked={isInternal}
                onCheckedChange={(val) => {
                    const checked = !!val;
                    setIsInternal(checked);
                    // if (!checked) {
                    //     setValidationError(null);
                    // }
                }}
                className="mt-1"/>
                <div className="flex-1">
                    <Label className="cursor-pointer">Is Internal?</Label>
                    <p className="text-xs text-gray-500 mt-1">
                        Internal listings are not visible to the public. They allow you to generate exclusive, tailored proposals for your clientele.
                    </p>
                </div>
            </div>
            {
                requireOwnerValidation && (
                    <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-4">
                        <Alert className='mb-4 border-blue-200 bg-blue-50'>
                            <Info className="h-4 w-4 text-blue-600" />
                            <AlertTitle className="text-blue-900">Owner Verification Required</AlertTitle>
                            <AlertDescription className="text-blue-800">
                                Please double-check all owner details carefully. Verification emails will be sent to the provided email addresses to confirm ownership. Ensure all information is accurate to avoid delays in the verification process.
                            </AlertDescription>
                        </Alert>
                        <Card className="p-4">
                            <div className="flex justify-between items-center mb-3">
                                <h5 className="font-semibold text-gray-700">Owner Details</h5>
                                {
                                    listing.published === 0 && (
                                        <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setOwnerDialog(true)
                                        }}>
                                            {owners.length === 0 ? (
                                                <>
                                                    <UserPlus className="w-4 h-4 mr-2" />
                                                    Add Owners
                                                </>
                                            ) : (
                                                <>
                                                    <Edit2 className="w-4 h-4 mr-2" />
                                                    Edit Owners
                                                </>
                                            )}
                                        </Button>
                                    )
                                }
                            </div>
                            {
                                owners.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500 border-2 border-dashed rounded-lg">
                                        <UserPlus className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                        <p className="text-sm">No owners added yet</p>
                                        <p className="text-xs mt-1">Click "Add Owners" to get started</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {owners.map((owner, index) => (
                                            <motion.div
                                                key={index}
                                                initial={{ opacity: 0, y: -10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: index * 0.1 }}
                                                className="border rounded-xl p-5 bg-gradient-to-br from-white to-gray-50 shadow-sm hover:shadow-md transition-shadow"
                                            >
                                                <div className="flex flex-col gap-3">
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <Avatar>
                                                                <AvatarImage src={`${import.meta.env.VITE_AWS_S3}/${owner.avatar}`} alt="@shadcn" />
                                                                <AvatarFallback>{owner.firstName[0]}{owner.lastName[0]}</AvatarFallback>
                                                            </Avatar>
                                                            <div>
                                                                <span className="font-semibold text-sm block">
                                                                    Owner {index + 1}
                                                                </span>
                                                                {getStatusBadge(owner.status)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    {owner.requestSentAt && (
                                                        <div className="flex items-center gap-1.5 text-xs text-gray-500 bg-gray-100 rounded-md px-2 py-1.5">
                                                            <Clock className="w-3 h-3" />
                                                            <span>{formatDateTime(owner.requestSentAt)}</span>
                                                        </div>
                                                    )}

                                                    <div className="space-y-2.5 text-sm pt-2 border-t">
                                                        <div className="flex items-start gap-2">
                                                            <span className="text-gray-500 min-w-[60px] text-xs">Name:</span>
                                                            <span className="font-medium text-gray-900 flex-1">
                                                                {owner.firstName} {owner.lastName}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-start gap-2">
                                                            <span className="text-gray-500 min-w-[60px] text-xs">Email:</span>
                                                            <span className="text-gray-700 flex-1 break-all text-xs">
                                                                {owner.email}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-start gap-2">
                                                            <span className="text-gray-500 min-w-[60px] text-xs">Contact:</span>
                                                            <span className="text-gray-700 flex-1 text-xs">
                                                                {owner.contact}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-start gap-2">
                                                            <span className="text-gray-500 min-w-[60px] text-xs">DOB:</span>
                                                            <span className="text-gray-700 flex-1 text-xs">
                                                                {formatDate(owner.dob)}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {owner.status === 0 && owner.reason && (
                                                        <Alert variant="destructive" className="mt-2 py-2">
                                                            <AlertCircle className="h-3.5 w-3.5" />
                                                            <AlertTitle className="text-xs font-semibold">Rejection Reason</AlertTitle>
                                                            <AlertDescription className="text-xs leading-relaxed">
                                                                {owner.reason}
                                                            </AlertDescription>
                                                        </Alert>
                                                    )}
                                                </div>
                                            </motion.div>
                                        ))}
                                    </div>
                                )
                            }
                        </Card>
                    </motion.div>
                )
            }
            <Dialog open={ownerDialog} onOpenChange={setOwnerDialog}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
                    <DialogHeader>
                        <DialogTitle>Owner Details</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-6 mt-4 overflow-y-auto max-h-[calc(90vh-120px)] px-1">
                        {
                            ownerError && (
                                <Alert variant="destructive">
                                    <AlertCircle className="h-4 w-4" />
                                    <AlertTitle>Error</AlertTitle>
                                    <AlertDescription>{ownerError}</AlertDescription>
                                </Alert>
                            )
                        }
                        {
                            owners.map((owner, ownerIndex) => (
                                <motion.div
                                key={ownerIndex}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="border rounded-lg p-6 bg-gray-50 relative">
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="font-semibold text-gray-700">
                                            Owner {ownerIndex + 1}
                                        </h3>
                                        {
                                            owners.length > 1 && (
                                                <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleRemoveOwner(ownerIndex)}
                                                className="text-red-600 hover:text-red-700 hover:bg-red-50">
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            )
                                        }
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <LabelWithAsterisk label="First Name" className="mb-2" />
                                            <Input
                                                type="text"
                                                placeholder="John"
                                                value={owner.firstName}
                                                onChange={(e) => handleOwnerChange(ownerIndex, 'firstName', e.target.value)}
                                                className={ownerValidationErrors[ownerIndex]?.includes('firstName') ? 'border-red-400' : ''}
                                            />
                                            <span className="text-red-500 text-xs">
                                                {getOwnerError(ownerIndex, 'firstName')}
                                            </span>
                                        </div>
                                        
                                        <div>
                                            <LabelWithAsterisk label="Last Name" className="mb-2" />
                                            <Input
                                                type="text"
                                                placeholder="Doe"
                                                value={owner.lastName}
                                                onChange={(e) => handleOwnerChange(ownerIndex, 'lastName', e.target.value)}
                                                className={ownerValidationErrors[ownerIndex]?.includes('lastName') ? 'border-red-400' : ''}
                                            />
                                            <span className="text-red-500 text-xs">
                                                {getOwnerError(ownerIndex, 'lastName')}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="mt-4">
                                        <LabelWithAsterisk label="Email" className="mb-2" />
                                        <Input
                                            type="email"
                                            placeholder="owner@example.com"
                                            value={owner.email}
                                            onChange={(e) => handleOwnerChange(ownerIndex, 'email', e.target.value)}
                                            className={
                                                ownerValidationErrors[ownerIndex]?.includes('email') || 
                                                ownerValidationErrors[ownerIndex]?.includes('email-format')
                                                    ? 'border-red-400' : ''
                                            }
                                        />
                                        <span className="text-red-500 text-xs">
                                            {getOwnerError(ownerIndex, 'email') || getOwnerError(ownerIndex, 'email-format')}
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 mt-4">
                                        <div>
                                            <LabelWithAsterisk label="Contact" className="mb-2" />
                                            <Input
                                                type="tel"
                                                placeholder="0123456789"
                                                value={owner.contact}
                                                onChange={(e) => handleOwnerChange(ownerIndex, 'contact', e.target.value)}
                                                className={
                                                    ownerValidationErrors[ownerIndex]?.includes('contact') || 
                                                    ownerValidationErrors[ownerIndex]?.includes('contact-format')
                                                        ? 'border-red-400' : ''
                                                }
                                            />
                                            <span className="text-red-500 text-xs">
                                                {getOwnerError(ownerIndex, 'contact') || getOwnerError(ownerIndex, 'contact-format')}
                                            </span>
                                        </div>
                                        
                                        <div>
                                            <LabelWithAsterisk label="Date of Birth" className="mb-2" />
                                            <DatePicker
                                                value={owner.dob}
                                                callback={(date) => {
                                                    if (date instanceof Date) handleOwnerChange(ownerIndex, 'dob', date);
                                                }}
                                                className={`w-full ${ownerValidationErrors[ownerIndex]?.includes('dob') ? 'border-red-400' : ''}`}
                                            />
                                            <span className="text-red-500 text-xs">
                                                {getOwnerError(ownerIndex, 'dob')}
                                            </span>
                                        </div>
                                    </div>
                                </motion.div>
                            ))
                        }
                        <Button
                        type="button"
                        variant="outline"
                        onClick={handleAddOwner}
                        className="w-full border-dashed border-2">
                            <Plus className="w-4 h-4 mr-2" />
                            Add Another Owner
                        </Button>
                        <div className="flex justify-end gap-2 pt-4 sticky bottom-0 bg-white pb-2">
                            <Button
                            variant="outline"
                            onClick={() => {
                                setOwnerDialog(false);
                                setOwnerError(null);
                                setOwnerValidationErrors({});
                            }}
                            size="sm">
                                Cancel
                            </Button>
                            <Button
                            onClick={handleOwnerSubmission}
                            className="signature-btn"
                            size="sm">
                                Submit
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </section>
    )
})

export default PlatformPostingDetails;